import { get } from "http";
import { create } from "zustand";
import { FormState, TransactionCertificateState } from "./IStore";
import { createJSONStorage, persist, PersistStorage, StateStorage } from 'zustand/middleware';
import * as CryptoJS from "crypto-js";

const isWeb = typeof window !== 'undefined';

const WebFallbackStorage: StateStorage = {
    getItem: async (name: string) => localStorage.getItem(name),
    setItem: async (name: string, value: string) => localStorage.setItem(name, value),
    removeItem: async (name: string) => localStorage.removeItem(name),
};

const SecureStorage: StateStorage = {
    getItem: async (name: string) => {
        const encryptedValue = localStorage.getItem(name);
        if (encryptedValue) {
            try {
                const bytes = CryptoJS.AES.decrypt(encryptedValue, "test");
                return bytes.toString(CryptoJS.enc.Utf8);
            } catch (error) {
                console.error("Failed to decrypt data:", error);
                return null;
            }
        }
        return null;
    },
    setItem: async (name: string, value: string) => {
        try {
            const encryptedValue = CryptoJS.AES.encrypt(value, "test").toString();
            localStorage.setItem(name, encryptedValue);
        } catch (error) {
            console.error("Failed to encrypt data:", error);
        }
    },
    removeItem: async (name: string) => {
        localStorage.removeItem(name);
    },
};

const localStorageWrapper: PersistStorage<any> = {
    getItem: (name) => {
        const item = localStorage.getItem(name);
        return item ? JSON.parse(item) : null;
    },
    setItem: (name, value) => {
        localStorage.setItem(name, JSON.stringify(value));
    },
    removeItem: (name) => {
        localStorage.removeItem(name);
    },
};


export const useFormStore = create(
    persist<FormState>(
        (set, get) => ({
            pages: {},
            setFormValues: (page, values) =>
                set((state) => {
                    // Create a new object with only serializable values
                    const serializableValues = Object.entries(values).reduce((acc, [key, value]) => {
                        // Skip React elements, DOM nodes, and functions
                        if (
                            value instanceof HTMLElement ||
                            value instanceof Function ||
                            (value && typeof value === 'object' && '$$typeof' in value)
                        ) {
                            return acc;
                        }
                        acc[key] = value;
                        return acc;
                    }, {} as Record<string, any>);

                    return {
                        pages: {
                            ...state.pages,
                            [page]: {
                                ...state.pages[page],
                                formvalues: {
                                    ...state.pages[page]?.formvalues,
                                    ...serializableValues,
                                },
                            },
                        },
                    };
                }),
            resetSpecificPage: (pageName: string, pageInitialState: any) =>
                set((state) => ({
                    pages: {
                        ...state.pages,
                        [pageName]: pageInitialState,
                    },
                })),
            setTableData: (page, data) =>
                set((state) => {
                    const existingTableData = state.pages[page]?.tableData || [];
                    const updatedTableData = [...existingTableData, ...data];
                    return {
                        pages: {
                            ...state.pages,
                            [page]: {
                                ...state.pages[page],
                                tableData: updatedTableData,
                                formvalues: state.pages[page]?.formvalues || {},
                            },
                        },
                    };
                }),
            removeTableRow: (page, index) =>
                set((state) => {
                    const tableData = state.pages[page]?.tableData || [];
                    if (index >= 0 && index < tableData.length) {
                        const updatedTableData = tableData.filter((_: any, i: any) => i !== index);
                        return {
                            pages: {
                                ...state.pages,
                                [page]: {
                                    ...state.pages[page],
                                    tableData: updatedTableData,
                                },
                            },
                        };
                    }
                    return state;
                }),

            getFormValues: (page) => {
                const pages = get().pages;
                return pages[page]?.formvalues || {};
            },
            removeTableRowByCondition: (page, condition) =>
                set((state) => {
                    const tableData = state.pages[page]?.tableData || [];
                    const updatedTableData = tableData.filter((row: any) => !condition(row));
                    return {
                        pages: {
                            ...state.pages,
                            [page]: {
                                ...state.pages[page],
                                tableData: updatedTableData,
                            },
                        },
                    };
                }),
            getTableData: (page) => {
                const pages = get().pages;
                return pages[page]?.tableData || [];
            },
            replaceTableData: (page, newData) =>
                set((state) => {
                    if (!state.pages[page]) {
                        console.warn(`Page "${page}" does not exist.`);
                        return state;
                    }
                    return {
                        pages: {
                            ...state.pages,
                            [page]: {
                                ...state.pages[page],
                                tableData: newData,
                            },
                        },
                    };
                }),
            setEditingRow: (page, index) =>
                set((state) => ({
                    pages: {
                        ...state.pages,
                        [page]: {
                            ...state.pages[page],
                            editingRowIndex: index,
                        },
                    },
                })),
            setEditSave: (page, value) =>
                set((state) => ({
                    pages: {
                        ...state.pages,
                        [page]: {
                            ...state.pages[page],
                            isEditSave: value,
                        },
                    },
                })),
            setNewTCSaved: (page, value) =>
                set((state) => ({
                    pages: {
                        ...state.pages,
                        [page]: {
                            ...state.pages[page],
                            isNewTCSaved: value,
                        },
                    },
                })),

            setDeleteSave: (page, value) =>
                set((state) => ({
                    pages: {
                        ...state.pages,
                        [page]: {
                            ...state.pages[page],
                            isDeleteSave: value,
                        },
                    },
                })),
            updateTableRow: (page, index, newData) =>
                set((state) => {
                    const tableData = state.pages[page]?.tableData || [];
                    if (index >= 0 && index < tableData.length) {
                        const updatedTableData = [...tableData];
                        updatedTableData[index] = newData;
                        return {
                            pages: {
                                ...state.pages,
                                [page]: {
                                    ...state.pages[page],
                                    tableData: updatedTableData,
                                },
                            },
                        };
                    }
                    return state;
                }),
            getEditingRow: (page) => get().pages[page]?.editingRowIndex || null,
            getEditSave: (page) => get().pages[page]?.isEditSave ?? false,
            getDeleteSave: (page) => get().pages[page]?.isDeleteSave ?? false,
            getNewTCSaved: (page) => get().pages[page]?.isNewTCSaved ?? false,

            resetPageFields: (page) =>
                set((state) => ({
                    pages: {
                        ...state.pages,
                        [page]: {
                            formvalues: {},
                        },
                    },
                })),

            resetAllDataByPage: (page) =>
                set((state) => ({
                    pages: {
                        ...state.pages,
                        [page]: {
                            formvalues: {},
                            tableData: null,
                            editingRowIndex: null
                        },
                    },
                })),

            resetAllPages: () =>
                set(() => ({
                    pages: {},
                })),
        }),
        {
            name: 'TE-Form-State',
            storage: localStorageWrapper,
        }
    )
);


export const useTransactionCertificateStore = create(persist<TransactionCertificateState>((set, get) => ({
    authToken: '',
    refreshTokenKey: '',
    tokenExpiry: '',
    setTokenExpiry: (tokenExpiry: string) => set(() => ({ tokenExpiry: tokenExpiry })),
    cbLicensingCode: '',
    setCbLicensingCode: (cbLicensingCode: string) => set(() => ({ cbLicensingCode: cbLicensingCode })),
    isNewTCRequested: false,
    isDelayResonSet: false,
    tcIssueDate: '',
    setAuthToken: (token: string) => set(() => ({ authToken: token })),
    setRefreshTokenKey: (refreshToken: string) => set(() => ({ refreshTokenKey: refreshToken })),
    setNewTCRequested: (status: boolean) => set(() => ({ isNewTCRequested: status })),
    isNewScRequested: false,
    setNewScRequested: (status: boolean) => set(() => ({ isNewScRequested: status })),
    setTCIssueDate: (date: string) => set(() => ({ tcIssueDate: date })),
    setDelayReason: (status: boolean) => set(() => ({ isDelayResonSet: status })),
    isRestrictPopUpSave: false,
    setRestrictPopUpSave: (status: boolean) => set(() => ({ isRestrictPopUpSave: status })),
    getEditTCInfo: {},
    setEditTCInfo: (data: any) => set(() => ({ getEditTCInfo: data })),
    getLabelGrades: {},
    setLabelGrades: (data: any) => set(() => ({ getLabelGrades: data })),
    getUserInfo: {},
    setUserInfo: (data: any) => set(() => ({ getUserInfo: data })),
    getCountries: [],
    setCountries: (data: any) => set(() => ({ getCountries: data })),
    getStates: [],
    setStates: (data: any) => set(() => ({ getStates: data })),
    getCBLicensingsCodes: [],
    setCBLicensingsCodes: (data: any) => set(() => ({ getCBLicensingsCodes: data })),
    getFacilityProcessCategoryCodes: [],
    setFacilityProcessCategoryCodes: (data: any) => set(() => ({ getFacilityProcessCategoryCodes: data })),
    getFPDStdLblGrd: [],
    setFPDStdLblGrd: (data: any) => set(() => ({ getFPDStdLblGrd: data })),
    getFPDPrdtCat: [],
    setFPDPrdtCat: (data: any) => set(() => ({ getFPDPrdtCat: data })),
    getFPDPrdtDtl: [],
    setFPDPrdtDtl: (data: any) => set(() => ({ getFPDPrdtDtl: data })),
    getFPDPrdtRawMtrlCodes: [],
    setFPDPrdtRawMtrlCodes: (data: any) => set(() => ({ getFPDPrdtRawMtrlCodes: data })),
    getTCStandardGOTS:false,
    setTCStandardGOTS: (data: boolean) => set(() => ({ getTCStandardGOTS: data })),
    getRawMtrlCodes:{},
    setRawMtrlCodes: (data: any) => set(() => ({ getRawMtrlCodes: data })),
    getCountryCodes:{},
    setCountryCodes: (data: any) => set(() => ({ getCountryCodes: data })),
    getProductDetailCodeStore:{},
    setProductDetailCodeStore: (data: any) => set(() => ({ getProductDetailCodeStore: data })),
    getOrgFarmStandards:{},
    setOrgFarmStandards: (data: any) => set(() => ({ getOrgFarmStandards: data })),
    getProductCategoriesStore:{},
    setProductCategoriesStore: (data: any) => set(() => ({ getProductCategoriesStore: data })),
}),
    {
        name: 'TE-App-State',
        storage: localStorageWrapper//createJSONStorage(() => SecureStorage),
    }
));